/**
 * 工作区成员管理页面
 * 提供成员列表展示、搜索、移除、邀请等功能
 */
import { useState, useMemo } from 'react'
import { Button, Input, toast } from '@you/ui'
import { DataTable } from '@/components/ui/data-table'
import { InviteLinkDialog } from '@/components/Workspace/InviteLinkDialog'
import { useWorkspaceMembers } from '@/api/hooks/useWorkspace'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import {
  useRemoveMemberAction,
  useInviteLinkAction,
  useClipboard,
} from '@/hooks/useWorkspaceMemberActions'
import {
  createMemberTableColumns,
  memberTablePresets,
} from '@/components/Workspace/memberTableColumns'
import { WorkspaceType } from '@/api/types/workspace.types'

/**
 * 页面头部操作区组件
 */
const PageHeader = ({
  onGenerateInvite,
  isGenerating,
  searchText,
  onSearchChange,
}: {
  onGenerateInvite: () => void
  isGenerating: boolean
  searchText: string
  onSearchChange: (value: string) => void
}) => {
  const { currentWorkspace } = useWorkspaceStore()
  const isPersonalSpace = currentWorkspace?.type === WorkspaceType.Personal

  return (
    <div className="mb-6">
      <div className="flex justify-between items-center">
        <div></div>
        <div className="flex items-center gap-4">
          <Input
            placeholder="搜索成员姓名或手机号..."
            value={searchText}
            onChange={(e) => onSearchChange(e.target.value)}
            style={{ width: '300px' }}
          />
          {!isPersonalSpace && (
            <Button onClick={onGenerateInvite} disabled={isGenerating}>
              {isGenerating ? '生成中...' : '生成邀请链接'}
            </Button>
          )}
        </div>
      </div>
    </div>
  )
}

/**
 * 工作区成员管理主组件
 */
const WorkspaceMember = () => {
  const { currentWorkspace } = useWorkspaceStore()
  const [searchText, setSearchText] = useState('')
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false)
  const [inviteUrl, setInviteUrl] = useState('')

  // API Hooks
  const {
    data: membersData,
    error,
    mutate,
  } = useWorkspaceMembers({
    workspaceId: Number(currentWorkspace?.id),
    search: searchText,
  })

  // 成员操作hooks
  const { removeMember, isRemoving } = useRemoveMemberAction(() => {
    toast.success('成员移除成功')
    mutate() // 重新获取成员列表
  })

  const { copyToClipboard } = useClipboard()

  const { generateInviteLink, isGenerating } = useInviteLinkAction((url) => {
    setInviteUrl(url)
    setInviteDialogOpen(true)
  })

  // 处理生成邀请链接按钮点击
  const handleGenerateInvite = () => {
    generateInviteLink()
  }

  // 数据处理
  const members = membersData?.members || []

  // 事件处理器
  const handleCopyInviteLink = async (url: string) => {
    const success = await copyToClipboard(url)
    if (success) {
      toast.success('邀请链接已复制到剪贴板')
    } else {
      toast.error('复制失败，请手动复制链接')
    }
  }

  // 表格列定义
  const columns = useMemo(
    () =>
      createMemberTableColumns(
        removeMember,
        isRemoving,
        memberTablePresets.full
      ),
    [removeMember, isRemoving]
  )

  // 列宽配置
  const columnWidths = {
    userName: 'calc(100% - 440px)', // 成员信息列占用剩余空间
    createTime: '240px', // 加入时间列固定宽度
    actions: '200px', // 操作列固定宽度
  }

  // 如果没有选择工作区，显示提示
  if (!currentWorkspace) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-gray-500 text-lg">请先选择一个工作区</p>
          <p className="text-gray-400 text-sm mt-2">在左侧选择要管理的工作区</p>
        </div>
      </div>
    )
  }

  // 如果加载出错，显示提示
  if (error) {
    return (
      <div className="space-y-4">
        <PageHeader
          onGenerateInvite={handleGenerateInvite}
          isGenerating={isGenerating}
          searchText={searchText}
          onSearchChange={setSearchText}
        />
      </div>
    )
  }

  return (
    <div className="flex-1 flex flex-col">
      <PageHeader
        onGenerateInvite={handleGenerateInvite}
        isGenerating={isGenerating}
        searchText={searchText}
        onSearchChange={setSearchText}
      />

      {/* 邀请链接对话框 */}
      <InviteLinkDialog
        open={inviteDialogOpen}
        onOpenChange={setInviteDialogOpen}
        inviteUrl={inviteUrl}
        onCopy={handleCopyInviteLink}
      />

      {/* 成员列表表格 */}
      <div className="rounded-lg">
        <DataTable
          transparent={true}
          columns={columns}
          data={members}
          columnWidths={columnWidths}
        />
      </div>
    </div>
  )
}

export default WorkspaceMember
